/** @jsxImportSource @emotion/react */

import { Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { createSearchParams, useLocation, useSearchParams } from 'react-router-dom'
import TabsNavigation from 'components/TabsNavigation/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import FAQ from 'pagesNew/FAQ'
import { FaqPages } from '__generatedTypes__/globalTypes'
import { PlatformSlug } from 'constants/consts'
import { RouteValue, useLink } from 'routes/cmsRoutes'
import * as classes from 'pagesNew/UpgradePage/styles'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import withPlatformCheck from 'HOCs/withPlatformCheck'
import NotFound from 'components/NotFound'
import { isFaqAvailable, isFaqPageAvailable } from './platformConfig'

export const TAB_QUERY_PARAM = 'tab'

const TABS: Array<{
  id: FaqPages
  title: string
}> = [
  { id: FaqPages.HomePage, title: 'Homepage' },
  { id: FaqPages.UpgradePage, title: 'Upgrade Page' },
  { id: FaqPages.AAMCPlugin, title: 'Plugin - Homepage' },
  { id: FaqPages.AAMCPluginHowToUse, title: 'Plugin - How to Use' },
  { id: FaqPages.CrashCourses, title: 'Crash Courses' },
  { id: FaqPages.FullTests, title: 'Full/Mini Tests' },
  { id: FaqPages.PracticeTestAnalytics, title: 'FLT/PT Analytics' },
  { id: FaqPages.Classroom, title: 'Classroom' },
  { id: FaqPages.AccountSettings, title: 'Account Settings' },
  { id: FaqPages.InfluencerHub, title: 'Influencer Hub' },
  { id: FaqPages.MCATScoreCalculation, title: 'MCAT Score Calculation' },
  { id: FaqPages.CheatSheetPreLaunch, title: 'Cheat Sheets Page' },
]

const getTabs = (platform: PlatformSlug) => TABS.filter(({ id }) => isFaqPageAvailable(platform, id))

type Props = {
  platform: Platform
}

export const FAQPage = withPlatformCheck(({ platform }: Props) => {
  const location = useLocation()
  const { navigate } = useLink()
  const [searchParams] = useSearchParams()
  const platformSlug = platform.slug as PlatformSlug

  const [activeTab, setActiveTab] = useState<TabsItem>(() => {
    const tabId = searchParams.get(TAB_QUERY_PARAM)
    const availableTabs = getTabs(platformSlug)

    return availableTabs.find(({ id }) => id === tabId) || availableTabs[0]
  })

  const handleChangeTab = (nextTab: TabsItem) => {
    navigate(location.pathname as RouteValue, undefined, {
      searchParams: createSearchParams({ [TAB_QUERY_PARAM]: nextTab.id }),
    })
    setActiveTab(nextTab)
  }

  const tabs = useMemo(() => getTabs(platformSlug), [platformSlug])

  if (!isFaqAvailable(platformSlug) || !tabs.length) {
    return <NotFound />
  }

  return (
    <section css={classes.container}>
      <Typography css={classes.header} variant="title3">
        FAQ
      </Typography>

      <TabsNavigation css={classes.tabs} tabs={tabs} value={activeTab} onChange={handleChangeTab} />

      <FAQ page={activeTab.id as FaqPages} platform={platform} />
    </section>
  )
})
